import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

const navLinks = [
  { label: "About", href: "#about", testId: "nav-link-about" },
  { label: "Companies", href: "#companies", testId: "nav-link-companies" },
  { label: "Favorites", href: "#featured", testId: "nav-link-featured" },
  { label: "Portfolio", href: "#portfolio", testId: "nav-link-portfolio" },
  { label: "Experience", href: "#experience", testId: "nav-link-experience" },
  { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export const Navbar = ({ profile }) => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ["about", "companies", "featured", "portfolio", "experience", "contact"];
      for (const section of [...sections].reverse()) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            return;
          }
        }
      }
      setActiveSection("");
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "nav-scrolled" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo / Name */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src={profile.logo_nav || profile.logo_url}
            alt="TYLEGN Logo"
            className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-110"
            width="40"
            height="40"
          />
          <span className="text-lg font-semibold text-foreground hidden sm:block" style={{ fontFamily: 'var(--font-display)' }}>
            Tommy Green
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              data-testid={link.testId}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                activeSection === link.href.replace("#", "")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-card border-border w-72">
              <div className="flex flex-col gap-2 mt-8">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    data-testid={`mobile-${link.testId}`}
                    className={`px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                      activeSection === link.href.replace("#", "")
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.nav>
  );
};
